import styled from 'styled-components';

import NotificationStyled from '../../../../../components/Notification';
import StickyNav from '../../../../../components/StickyNav';
import { IconInfoCircle as InfoCircle } from '../../../../../core';
import UseCasePalettes from '../../../components/UseCasePalettes';

interface Props {
  className?: string;
}

const UseCaseTab = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="tab-content">
      <div className="definition">
        <h2 className="title" id="definition">
          Definition
        </h2>
        <p>
          A data visualization tool that represents categorical data using rectangular bars oriented
          vertically. The length or height of each bar corresponds to the value or magnitude of the
          data it represents, making it easy to compare different categories at a glance. Vertical
          bar charts are ideal for showcasing discrete data, tracking trends over time (when
          categories represent sequential periods), or highlighting differences among groups in a
          visually straightforward manner.
        </p>
      </div>
      <div className="usage">
        <h2 className="title" id="usage">
          Usage
        </h2>
        <p className="subtitle">
          Vertical bar charts are ideal for comparing the size or frequency of categories within a
          dataset. They are particularly useful when:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Comparing Discrete Categories:</strong> Vertical bar charts help compare data
            across distinct categories, such as sales by product, revenue by region, or population
            by age group.
          </li>
          <li>
            <strong>Tracking Changes Over Time:</strong> When categories represent time periods
            (e.g., months, years), vertical bar charts are effective in visualizing trends, such as
            annual profits or monthly website visits.
          </li>
          <li>
            <strong>Highlighting Rankings or Differences:</strong> Vertical bar charts can clearly
            display rankings or differences in data, making them great for visualizing
            top-performing items or categories, like the best-selling products or top countries by
            GDP.
          </li>
        </ul>
      </div>
      <div className="limitations">
        <h2 className="title" id="limitations">
          Limitations
        </h2>
        <p className="subtitle">
          Vertical bar charts are a powerful visualization tool, but they come with limitations that
          must be considered when selecting them for specific data sets or analyses:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Limited Data Representation for Large Datasets:</strong> When dealing with a
            large number of categories or data series, vertical bar charts can become cluttered,
            making it difficult to interpret the data clearly, especially if the bars are too close
            together.
          </li>
          <li>
            <strong>Challenges with Category Labeling:</strong> Long or numerous category labels can
            overlap or become unreadable, making the chart harder to understand. This can reduce the
            effectiveness of the chart, particularly when trying to convey precise information.
          </li>
          <li>
            <strong>Not Ideal for Time Series Data:</strong> Vertical bar charts may not be the most
            effective choice for displaying time-based data or trends. Line or area charts typically
            provide better clarity for visualizing temporal changes and patterns over time.
          </li>
        </ul>
      </div>
      <div className="colors">
        <h2 className="title" id="colors">
          Colors
        </h2>
        <p>
          Color selection is vital in vertical bar charts to enhance readability and emphasize data
          differences. Bold, distinct colors differentiate categories, making comparisons
          straightforward, while neutral tones can highlight overall trends without overwhelming the
          viewer. Gradual color gradients represent intensity or progression, ensuring the chart
          remains visually engaging and informative. Thoughtful color choices guide focus, prevent
          visual clutter, and improve the chart&apos;s effectiveness in communicating insights.
        </p>
        <NotificationStyled
          icon={<InfoCircle />}
          title="For in-depth information on this topic, please refer to the Colors page."
          color="blue"
          className="info-notification"
        />
        <UseCasePalettes palettes={['qualitative']} />
      </div>

      <div className="challenges">
        <h2 className="title">Challenges</h2>
        <p>
          Vertical bar charts are a widely used tool for comparing categorical data, but they come
          with several challenges that should be addressed:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Difficulty in Comparing Data Across Categories:</strong> When bars are similar
            in height, it becomes hard to quickly compare values across categories, especially when
            the chart has many bars.
          </li>
          <li>
            <strong>Limited Space for Labeling:</strong> Category labels can overlap or become
            unreadable when there is not enough space, especially with long or numerous labels.
          </li>
          <li>
            <strong>Ineffective for Large Data Sets:</strong> Vertical bar charts can become
            cluttered with too many categories, making it harder to interpret the data clearly and
            find meaningful insights.
          </li>
        </ul>
      </div>
    </div>
    <div>
      <StickyNav title="Contents" />
    </div>
  </div>
);

const StyledUseCaseTab = styled(UseCaseTab)`
  display: flex;
  gap: 2.5rem;
  justify-content: space-between;

  > .tab-content {
    width: 100%;
    .usage,
    .definition,
    .limitations,
    .challenges {
      max-width: 700px;
    }
    > .colors {
      > .info-notification {
        margin-top: 24px;
        margin-bottom: 40px;
      }

      > .palette {
        > .palette-title {
          color: ${({ theme }) => theme.neutralText};
          font-size: ${({ theme }) => theme.fontSizes.base};
          line-height: ${({ theme }) => theme.lineHeights.base};
          margin-bottom: 4px;
        }
      }

      .color-palette {
        margin-top: 12px;
      }
    }

    > .challenges {
      > .ordered-list-badge-num {
        margin-top: 16px;
      }
    }

    .title {
      color: ${({ theme }) => theme.neutralText};
      font-weight: 600;
      font-size: ${({ theme }) => theme.fontSizes.lg};
      line-height: ${({ theme }) => theme.lineHeights.lg};
      margin-bottom: 8px;
      margin-top: 40px;
    }

    p {
      color: ${({ theme }) => theme.neutralTextWeak};
      font-size: ${({ theme }) => theme.fontSizes.sm};
      line-height: ${({ theme }) => theme.lineHeights.sm};
      font-weight: 400;
      max-width: 700px;
    }

    .subtitle {
      margin-bottom: 16px;
    }

    // styles for list with badge
    .ordered-list-badge-num {
      font-size: 0.875rem;
      line-height: 1.4285714286;
      margin-left: 29px;
      counter-reset: num;
      list-style: none;
      color: ${({ theme }) => theme.neutralTextWeak};

      li {
        position: relative;
        margin-bottom: 16px;

        &::before {
          content: counter(num);
          counter-increment: num;
          position: absolute;
          left: -1.75rem;
          top: 0;
          width: 1.25rem;
          height: 1.25rem;
          border-radius: 0.1875rem;
          text-align: center;
          font-size: 0.6875rem;
          line-height: 1.8;
          color: ${({ theme }) => theme.primaryText};
          background-color: ${({ theme }) => theme.primaryBackgroundActive};
        }
      }
    }
  }
`;

export default StyledUseCaseTab;
